if (!customElements.get('bb-smart-search')) {
  customElements.define('bb-smart-search', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.input = this.querySelector('input[name="q"]');
      this.form = this.querySelector('form');
      this.panel = this.querySelector('.bb-smart-search__dropdown');
      this.results = this.querySelector('.bb-smart-search__results');
      this.message = this.querySelector('[data-search-message]');
      this.status = this.querySelector('[data-search-status]');
      this.clearButton = this.querySelector('.bb-smart-search__clear');
      this.cache = new Map();
      this.sequence = 0;
      this.activeIndex = -1;
      this.clearButton.hidden = !this.input.value;
      this.input.addEventListener('input', event => { if (!event.isComposing) this.onInput(); });
      this.input.addEventListener('compositionend', () => this.onInput());
      this.input.addEventListener('focus', () => { if (this.input.value.trim().length >= 2) this.onInput(); });
      this.input.addEventListener('keydown', event => this.onKeydown(event));
      this.form.addEventListener('submit', () => this.close());
      this.clearButton.addEventListener('click', () => {
        this.input.value = '';
        this.onInput();
        this.input.focus();
      });
      this.results.addEventListener('pointermove', event => {
        const option = event.target.closest('[data-search-option]');
        if (option) this.setActive(this.options.indexOf(option), false);
      });
      this.addEventListener('focusout', event => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.close();
      });
      this.outsideClick = event => { if (!this.contains(event.target)) this.close(); };
      document.addEventListener('pointerdown', this.outsideClick);
      this.escape = event => { if (event.key === 'Escape' && this.contains(event.target)) this.close(); };
      document.addEventListener('keydown', this.escape);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.controller?.abort();
      document.removeEventListener('pointerdown', this.outsideClick);
      document.removeEventListener('keydown', this.escape);
    }

    get options() {
      return Array.from(this.results.querySelectorAll('[data-search-option]'));
    }

    onInput() {
      clearTimeout(this.timer);
      this.controller?.abort();
      const sequence = ++this.sequence;
      const query = this.input.value.trim();
      this.clearButton.hidden = !this.input.value;
      this.close();
      this.dismissed = false;
      this.results.replaceChildren();
      if (query.length < 2) {
        this.results.replaceChildren();
        this.status.textContent = '';
        return;
      }
      const cached = this.cache.get(query.toLowerCase());
      if (cached) {
        this.render(cached);
        return;
      }
      this.timer = setTimeout(() => this.search(query, sequence), 240);
    }

    async search(query, sequence) {
      this.controller = new AbortController();
      const controller = this.controller;
      const timeout = setTimeout(() => controller.abort(), 7000);
      this.results.replaceChildren();
      this.message.textContent = this.dataset.loading;
      this.message.hidden = false;
      this.status.textContent = this.dataset.loading;
      this.results.setAttribute('aria-busy', 'true');
      if (document.activeElement === this.input && !this.dismissed) this.open();
      try {
        const url = new URL(this.dataset.endpoint, window.location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', 'bb-predictive-search');
        url.searchParams.set('resources[type]', 'product,collection,query');
        url.searchParams.set('resources[limit]', '6');
        url.searchParams.set('resources[limit_scope]', 'each');
        url.searchParams.set('resources[options][unavailable_products]', 'hide');
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Search unavailable');
        const html = await response.text();
        if (sequence !== this.sequence || query !== this.input.value.trim()) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const content = doc.querySelector('[data-bb-search-content]');
        if (!content) throw new Error('Search unavailable');
        this.cache.set(query.toLowerCase(), content.innerHTML);
        if (this.cache.size > 20) this.cache.delete(this.cache.keys().next().value);
        this.render(content.innerHTML);
      } catch (error) {
        if (sequence !== this.sequence || (error.name === 'AbortError' && this.input.value.trim() !== query)) return;
        this.results.replaceChildren();
        this.message.hidden = false;
        this.message.textContent = this.dataset.unavailable;
        this.status.textContent = this.dataset.unavailable;
        if (document.activeElement === this.input && !this.dismissed) this.open();
      } finally {
        clearTimeout(timeout);
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }

    render(html) {
      this.results.innerHTML = html;
      this.message.hidden = true;
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.options.forEach((option, index) => {
        option.id = `${this.results.id}-option-${index}`;
        option.setAttribute('aria-selected', 'false');
      });
      this.status.textContent = this.dataset.ready;
      if (document.activeElement === this.input && !this.dismissed) this.open();
    }

    open() {
      this.dismissed = false;
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.querySelectorAll('details[open]').forEach(menu => { menu.open = false; });
    }

    close() {
      this.dismissed = true;
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeIndex = -1;
      this.options.forEach(option => option.setAttribute('aria-selected', 'false'));
    }

    setActive(index, scroll = true) {
      const options = this.options;
      if (!options.length) return;
      this.activeIndex = (index + options.length) % options.length;
      options.forEach((option, current) => option.setAttribute('aria-selected', current === this.activeIndex ? 'true' : 'false'));
      const active = options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      if (scroll) active.scrollIntoView({ block: 'nearest' });
    }

    onKeydown(event) {
      if (event.isComposing) return;
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && this.options.length) {
        event.preventDefault();
        this.open();
        this.setActive(this.activeIndex < 0 ? (event.key === 'ArrowDown' ? 0 : this.options.length - 1) : this.activeIndex + (event.key === 'ArrowDown' ? 1 : -1));
      } else if (event.key === 'Enter' && !this.panel.hidden && this.activeIndex >= 0) {
        const selected = this.options[this.activeIndex];
        if (selected) {
          event.preventDefault();
          selected.click();
        }
      } else if (event.key === 'Tab') {
        this.close();
      }
    }
  });
}
